import { useFeatureFlag, useIsMobile } from '@newfront-insurance/admin-ui';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useMemo } from 'react';

import { ChatContainer } from '../components/chat-container';
import { ChatContent } from '../components/chat-content';
import { AccountSelector, ChatHeader, NewChatButton, ToggleSidebar } from '../components/chat-header';
import { ChatHeaderActions } from '../components/chat-header/chat-header-actions';
import { ChatRoot } from '../components/chat-root';
import {
  ChatSidebar,
  ChatSidebarFilterTabs,
  ChatSidebarHistory,
  ChatSidebarNewChat,
  ChatSidebarSearch,
  ChatSidebarSettingsButton,
} from '../components/chat-sidebar';
import { ArtifactPreviewProvider } from '../components/tools/artifact-preview/artifact-preview-provider';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { createDefaultDataPartRenderers, createDefaultToolRenderers } from '../lib/default-renderers';
import { NovaChatProvider } from '../providers';
import { NavigatorGraphPayload } from './navigator-graph-payload';

/**
 * Container component that renders the chat UI.
 * Reads all props from context - no prop drilling needed.
 * All child components are self-contained and manage their own state.
 */
export function NovaChatContainer() {
  const {
    isVisible,
    authProvider,
    apiEndpoint,
    userUuid,
    accountUuid,
    userScopes: scopes,
    chatUuid,
    initialMessages,
    isLoadingMessages: loading,
    placeholder,
    isStandaloneMode,
    isSidebarOpen,
    isFullscreen,
  } = useChatOverlayContext();
  const isNovaSourcesRenderingEnabled = useFeatureFlag('nova-sources-rendering', false);
  const { isMobile } = useIsMobile();
  const desktopDockedSidebarClasses = !isMobile && !isFullscreen ? 'rounded-tr-sm rounded-tl-none' : undefined;

  // Create default renderers (memoized to avoid recreating on every render)
  const toolRenderers = useMemo(() => createDefaultToolRenderers(), []);
  const dataPartRenderers = useMemo(() => createDefaultDataPartRenderers(), []);

  return (
    <ChatContainer isVisible={isVisible} className={desktopDockedSidebarClasses}>
      <NovaChatProvider
        authProvider={authProvider}
        userUuid={userUuid}
        accountUuid={accountUuid}
        apiEndpoint={apiEndpoint}
        scopes={scopes}
        initialMessages={initialMessages}
      >
        <NavigatorGraphPayload />
        <ChatSidebar>
          <ChatSidebarNewChat />
          <ChatSidebarSettingsButton />
          <ChatSidebarSearch />
          <ChatSidebarFilterTabs />
          <ChatSidebarHistory />
        </ChatSidebar>
        <ArtifactPreviewProvider
          className={cn('h-full min-w-0 transition-[margin-left] duration-200', {
            'ml-[253px]': (isStandaloneMode || isFullscreen) && isSidebarOpen && !isMobile,
            'ml-[63px]': (isStandaloneMode || isFullscreen) && !isSidebarOpen && !isMobile,
          })}
          contentClassName="size-full min-w-0"
        >
          <ChatRoot key={chatUuid}>
            <ChatHeader className={desktopDockedSidebarClasses}>
              {isMobile ? (
                <>
                  <ToggleSidebar />
                  <div className="flex flex-1 justify-center">
                    <AccountSelector />
                  </div>
                  <NewChatButton />
                </>
              ) : (
                <>
                  <AccountSelector />
                  <ChatHeaderActions />
                </>
              )}
            </ChatHeader>
            <ChatContent
              isLoading={loading}
              toolRenderers={toolRenderers}
              dataPartRenderers={dataPartRenderers}
              placeholder={placeholder}
              enableSourcesRendering={isNovaSourcesRenderingEnabled}
              withArtifactPreviewProvider={false}
            />
          </ChatRoot>
        </ArtifactPreviewProvider>
      </NovaChatProvider>
    </ChatContainer>
  );
}
